import { useState } from "react";
import { Upload, Save, X, Trash2, Star, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { adminCreateProperty, adminUpdateProperty } from "../../lib/api";

const CATEGORIES = ["Apartment", "Penthouse", "Townhouse", "Studio", "Mews House", "Villa"];
const BADGES = ["", "Available Now", "Reserved", "High Demand", "Limited Availability", "Coming Soon"];
const CANCELLATION_POLICIES = ["Flexible", "Moderate", "Strict", "Non-refundable"];

const FieldLabel = ({ children, hint }) => (
  <div className="mb-1.5">
    <label className="block text-[0.62rem] uppercase tracking-[0.22em] text-[var(--ed-ink-muted)]">{children}</label>
    {hint && <p className="text-[0.7rem] text-[var(--ed-ink-muted)] mt-0.5">{hint}</p>}
  </div>
);

const Input = (props) => (
  <input
    {...props}
    className={
      "w-full bg-[var(--ed-bg)] border border-[var(--ed-rule)] focus:border-[var(--ed-emerald)] " +
      "text-[var(--ed-ink)] px-3 py-2.5 text-sm outline-none rounded-sm transition-colors " +
      (props.className || "")
    }
  />
);

const Textarea = (props) => (
  <textarea
    {...props}
    className={
      "w-full bg-[var(--ed-bg)] border border-[var(--ed-rule)] focus:border-[var(--ed-emerald)] " +
      "text-[var(--ed-ink)] px-3 py-2.5 text-sm outline-none rounded-sm transition-colors resize-none " +
      (props.className || "")
    }
  />
);

const Select = (props) => (
  <select
    {...props}
    className={
      "w-full bg-[var(--ed-bg)] border border-[var(--ed-rule)] focus:border-[var(--ed-emerald)] " +
      "text-[var(--ed-ink)] px-3 py-2.5 text-sm outline-none rounded-sm transition-colors " +
      (props.className || "")
    }
  >
    {props.children}
  </select>
);

const emptyProperty = {
  name: "", location: "", description: "",
  bedrooms: 1, bathrooms: 1, guests: 2,
  price_per_night: 100, cleaning_fee: 0,
  images: [], amenities: [], house_rules: [],
  featured: false, bookable: false, published: true,
  category: "Apartment", badge: "",
  address: "", slug: "",
  latitude: null, longitude: null,
  check_in_time: "15:00", check_out_time: "11:00",
  cancellation_policy: "Flexible", external_booking_url: "",
  seo_title: "", seo_description: "", og_image: "",
};

export { emptyProperty };

export default function PropertyEditor({ property, onClose, onSaved }) {
  const isNew = !property?.id;
  const [form, setForm] = useState({
    ...emptyProperty,
    ...property,
    amenities: property?.amenities || [],
    images: property?.images || [],
    house_rules: property?.house_rules || [],
  });
  const [amenInput, setAmenInput] = useState("");
  const [ruleInput, setRuleInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [activeTab, setActiveTab] = useState("basics");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // ---- Image upload + DnD ordering + cover selection ----
  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let ok = 0;
    const errors = [];
    try {
      for (const f of files) {
        try {
          const fd = new FormData();
          fd.append("file", f);
          const r = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setForm((prev) => ({ ...prev, images: [...prev.images, r.data.url] }));
          ok += 1;
        } catch (err) {
          const detail = err?.response?.data?.detail || err?.message || "Upload failed";
          errors.push(`${f.name}: ${detail}`);
        }
      }
      if (ok) toast.success(`Uploaded ${ok} of ${files.length} photo${files.length > 1 ? "s" : ""}`);
      errors.forEach((m) => toast.error(m));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  const makeCover = (idx) => {
    if (idx === 0) return;
    setForm((f) => {
      const imgs = [...f.images];
      const [picked] = imgs.splice(idx, 1);
      imgs.unshift(picked);
      return { ...f, images: imgs };
    });
  };
  const handleDragStart = (i) => setDragIndex(i);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (i) => {
    if (dragIndex === null || dragIndex === i) return;
    setForm((f) => {
      const imgs = [...f.images];
      const [moved] = imgs.splice(dragIndex, 1);
      imgs.splice(i, 0, moved);
      return { ...f, images: imgs };
    });
    setDragIndex(null);
  };

  // ---- Amenities + house rules ----
  const addAmenity = () => {
    const v = amenInput.trim();
    if (!v) return;
    setForm((f) => ({ ...f, amenities: [...f.amenities, v] }));
    setAmenInput("");
  };
  const removeAmenity = (i) => setForm((f) => ({ ...f, amenities: f.amenities.filter((_, j) => j !== i) }));

  const addRule = () => {
    const v = ruleInput.trim();
    if (!v) return;
    setForm((f) => ({ ...f, house_rules: [...f.house_rules, v] }));
    setRuleInput("");
  };
  const removeRule = (i) => setForm((f) => ({ ...f, house_rules: f.house_rules.filter((_, j) => j !== i) }));

  // ---- Submit ----
  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    const payload = {
      name: form.name,
      location: form.location,
      description: form.description,
      bedrooms: parseInt(form.bedrooms || 1, 10),
      bathrooms: parseInt(form.bathrooms || 1, 10),
      guests: parseInt(form.guests || 2, 10),
      price_per_night: parseInt(form.price_per_night || 0, 10),
      cleaning_fee: form.cleaning_fee === "" || form.cleaning_fee === null ? null : parseInt(form.cleaning_fee, 10),
      images: form.images,
      amenities: form.amenities,
      house_rules: form.house_rules,
      featured: !!form.featured,
      category: form.category,
      bookable: !!form.bookable,
      published: !!form.published,
      badge: form.badge || null,
      address: form.address || null,
      slug: form.slug || null,
      latitude: num(form.latitude),
      longitude: num(form.longitude),
      check_in_time: form.check_in_time || null,
      check_out_time: form.check_out_time || null,
      cancellation_policy: form.cancellation_policy || null,
      external_booking_url: form.external_booking_url || null,
      seo_title: form.seo_title || null,
      seo_description: form.seo_description || null,
      og_image: form.og_image || null,
    };
    try {
      if (isNew) await adminCreateProperty(payload);
      else await adminUpdateProperty(property.id, payload);
      toast.success(isNew ? "Property created" : "Property updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const TABS = [
    { key: "basics", label: "Basics" },
    { key: "media", label: "Photos" },
    { key: "details", label: "Details" },
    { key: "policy", label: "Stay Policy" },
    { key: "seo", label: "SEO" },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
      <form
        onSubmit={onSubmit}
        data-testid="property-editor"
        className="w-full max-w-4xl my-8 bg-[var(--ed-bg)] border border-[var(--ed-rule)] rounded-sm shadow-2xl"
      >
        {/* Header */}
        <div className="px-7 pt-7 pb-5 border-b border-[var(--ed-rule)] flex items-start justify-between gap-4">
          <div>
            <p className="ed-overline mb-1">{isNew ? "Add residence" : "Edit residence"}</p>
            <h2 className="font-serif text-2xl text-[var(--ed-ink)]">
              {form.name || (isNew ? "Untitled residence" : "Untitled")}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-xs text-[var(--ed-ink-soft)] cursor-pointer pr-3 border-r border-[var(--ed-rule)]">
              <input
                data-testid="editor-published"
                type="checkbox"
                checked={!!form.published}
                onChange={(e) => set("published", e.target.checked)}
                className="w-4 h-4 accent-[var(--ed-emerald)]"
              />
              <span className="uppercase tracking-[0.2em] text-[0.62rem]">
                {form.published ? "Active" : "Inactive"}
              </span>
            </label>
            <button
              type="button"
              data-testid="editor-close"
              onClick={onClose}
              className="text-[var(--ed-ink-muted)] hover:text-[var(--ed-ink)] p-1"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 px-7 pt-4 border-b border-[var(--ed-rule)] bg-[var(--ed-paper)]/40">
          {TABS.map((t) => (
            <button
              type="button"
              key={t.key}
              data-testid={`editor-tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              className={`text-[0.65rem] uppercase tracking-[0.24em] px-4 py-2.5 border-b-2 -mb-px transition-colors ${
                activeTab === t.key
                  ? "border-[var(--ed-emerald)] text-[var(--ed-emerald)]"
                  : "border-transparent text-[var(--ed-ink-muted)] hover:text-[var(--ed-ink)]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-7 space-y-6">
          {activeTab === "basics" && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FieldLabel>Name</FieldLabel>
                  <Input data-testid="editor-name" required value={form.name} onChange={(e) => set("name", e.target.value)} />
                </div>
                <div>
                  <FieldLabel>Location</FieldLabel>
                  <Input data-testid="editor-location" required value={form.location} onChange={(e) => set("location", e.target.value)} />
                </div>
              </div>

              <div>
                <FieldLabel>Description</FieldLabel>
                <Textarea data-testid="editor-desc" required rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <FieldLabel>Bedrooms</FieldLabel>
                  <Input data-testid="editor-bedrooms" type="number" min="0" value={form.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} />
                </div>
                <div>
                  <FieldLabel>Bathrooms</FieldLabel>
                  <Input data-testid="editor-bathrooms" type="number" min="0" value={form.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} />
                </div>
                <div>
                  <FieldLabel>Max guests</FieldLabel>
                  <Input data-testid="editor-guests" type="number" min="1" value={form.guests} onChange={(e) => set("guests", e.target.value)} />
                </div>
                <div>
                  <FieldLabel>£ / night</FieldLabel>
                  <Input data-testid="editor-price" type="number" min="0" value={form.price_per_night} onChange={(e) => set("price_per_night", e.target.value)} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <FieldLabel>Category</FieldLabel>
                  <Select data-testid="editor-category" value={form.category} onChange={(e) => set("category", e.target.value)}>
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </Select>
                </div>
                <div>
                  <FieldLabel>Status badge</FieldLabel>
                  <Select data-testid="editor-badge" value={form.badge || ""} onChange={(e) => set("badge", e.target.value)}>
                    {BADGES.map((b) => <option key={b || "none"} value={b}>{b || "— None —"}</option>)}
                  </Select>
                </div>
                <div>
                  <FieldLabel>Cleaning fee (£)</FieldLabel>
                  <Input data-testid="editor-cleaning" type="number" min="0" value={form.cleaning_fee ?? ""} onChange={(e) => set("cleaning_fee", e.target.value)} />
                </div>
              </div>

              <div className="flex flex-wrap gap-5 pt-2">
                <label className="flex items-center gap-2 text-[var(--ed-ink-soft)] text-sm">
                  <input data-testid="editor-bookable" type="checkbox" checked={form.bookable} onChange={(e) => set("bookable", e.target.checked)} className="w-4 h-4 accent-[var(--ed-emerald)]" />
                  Online bookable (Stripe)
                </label>
                <label className="flex items-center gap-2 text-[var(--ed-ink-soft)] text-sm">
                  <input data-testid="editor-featured" type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="w-4 h-4 accent-[var(--ed-emerald)]" />
                  Featured on homepage
                </label>
              </div>
            </>
          )}

          {activeTab === "media" && (
            <div>
              <FieldLabel hint="Drag to reorder. The first image is the cover used in cards, galleries and OG previews. Click ★ on any photo to promote it to cover.">Photographs</FieldLabel>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-3">
                {form.images.map((url, i) => (
                  <div
                    key={`${url}-${i}`}
                    draggable
                    onDragStart={() => handleDragStart(i)}
                    onDragOver={handleDragOver}
                    onDrop={() => handleDrop(i)}
                    className="relative aspect-[4/3] border border-[var(--ed-rule)] group cursor-move bg-[var(--ed-paper)]"
                    data-testid={`editor-img-${i}`}
                  >
                    <img src={url} alt="" className="w-full h-full object-cover pointer-events-none" />
                    <div className="absolute inset-0 bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      {i !== 0 && (
                        <button
                          type="button"
                          data-testid={`editor-cover-${i}`}
                          onClick={() => makeCover(i)}
                          className="bg-white/95 text-[var(--ed-ink)] text-[0.6rem] tracking-widest uppercase px-2 py-1 hover:bg-[var(--ed-emerald)] hover:text-white"
                          title="Make cover"
                        >
                          <Star size={11} />
                        </button>
                      )}
                      <button
                        type="button"
                        data-testid={`editor-remove-img-${i}`}
                        onClick={() => removeImage(i)}
                        className="bg-red-600/90 text-white text-[0.6rem] px-2 py-1 hover:bg-red-700"
                        title="Remove"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                    <span className="absolute top-1.5 left-1.5 bg-[var(--ed-ink)]/70 text-white p-1 rounded-sm">
                      <GripVertical size={11} />
                    </span>
                    {i === 0 && (
                      <span className="absolute bottom-1.5 left-1.5 bg-[var(--ed-emerald)] text-white text-[0.55rem] tracking-[0.22em] uppercase font-semibold px-1.5 py-0.5">
                        Cover
                      </span>
                    )}
                  </div>
                ))}
                <label
                  data-testid="editor-upload-zone"
                  className="aspect-[4/3] border-2 border-dashed border-[var(--ed-rule)] hover:border-[var(--ed-emerald)] cursor-pointer flex flex-col items-center justify-center gap-2 text-[var(--ed-ink-muted)] hover:text-[var(--ed-emerald)] text-xs transition-colors"
                >
                  <Upload size={20} />
                  {uploading ? "Uploading…" : "Add photos"}
                  <input
                    data-testid="editor-upload-input"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                    multiple
                    onChange={onUpload}
                    className="hidden"
                  />
                </label>
              </div>
              <p className="text-[0.7rem] text-[var(--ed-ink-muted)] mt-2">
                HEIC/HEIF (iPhone) photos are automatically converted to optimised JPEG.
              </p>
            </div>
          )}

          {activeTab === "details" && (
            <>
              <div>
                <FieldLabel>Slug (URL fragment)</FieldLabel>
                <Input data-testid="editor-slug" value={form.slug || ""} placeholder="hammersmith-studio-w6" onChange={(e) => set("slug", e.target.value)} />
              </div>
              <div>
                <FieldLabel>Full address (internal)</FieldLabel>
                <Input data-testid="editor-address" value={form.address || ""} onChange={(e) => set("address", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <FieldLabel>Latitude</FieldLabel>
                  <Input data-testid="editor-lat" type="number" step="any" value={form.latitude ?? ""} onChange={(e) => set("latitude", e.target.value)} />
                </div>
                <div>
                  <FieldLabel>Longitude</FieldLabel>
                  <Input data-testid="editor-lng" type="number" step="any" value={form.longitude ?? ""} onChange={(e) => set("longitude", e.target.value)} />
                </div>
              </div>

              <div>
                <FieldLabel>Amenities</FieldLabel>
                <div className="flex gap-2 mb-3">
                  <Input
                    data-testid="editor-amenity-input"
                    value={amenInput}
                    onChange={(e) => setAmenInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAmenity(); } }}
                    placeholder="Add amenity"
                  />
                  <button type="button" data-testid="editor-add-amenity" onClick={addAmenity} className="text-[0.65rem] uppercase tracking-[0.22em] px-4 py-2 border border-[var(--ed-ink)] text-[var(--ed-ink)] hover:bg-[var(--ed-ink)] hover:text-[var(--ed-bg)] rounded-sm">
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {form.amenities.map((a, i) => (
                    <span key={i} className="text-xs px-3 py-1.5 bg-[var(--ed-paper)] border border-[var(--ed-rule)] text-[var(--ed-ink-soft)] flex items-center gap-2 rounded-sm">
                      {a}
                      <button type="button" data-testid={`editor-remove-amen-${i}`} onClick={() => removeAmenity(i)} className="text-[var(--ed-ink-muted)] hover:text-red-500">
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}

          {activeTab === "policy" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <FieldLabel>Check-in time</FieldLabel>
                  <Input data-testid="editor-checkin" type="time" value={form.check_in_time || ""} onChange={(e) => set("check_in_time", e.target.value)} />
                </div>
                <div>
                  <FieldLabel>Check-out time</FieldLabel>
                  <Input data-testid="editor-checkout" type="time" value={form.check_out_time || ""} onChange={(e) => set("check_out_time", e.target.value)} />
                </div>
              </div>
              <div>
                <FieldLabel>Cancellation policy</FieldLabel>
                <Select data-testid="editor-cancellation" value={form.cancellation_policy || "Flexible"} onChange={(e) => set("cancellation_policy", e.target.value)}>
                  {CANCELLATION_POLICIES.map((p) => <option key={p}>{p}</option>)}
                </Select>
              </div>
              <div>
                <FieldLabel>External booking URL (when not Stripe-bookable)</FieldLabel>
                <Input data-testid="editor-ext-url" value={form.external_booking_url || ""} placeholder="https://airbnb.com/…" onChange={(e) => set("external_booking_url", e.target.value)} />
              </div>
              <div>
                <FieldLabel>House rules</FieldLabel>
                <div className="flex gap-2 mb-3">
                  <Input
                    data-testid="editor-rule-input"
                    value={ruleInput}
                    onChange={(e) => setRuleInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRule(); } }}
                    placeholder="No smoking · Quiet after 10pm"
                  />
                  <button type="button" data-testid="editor-add-rule" onClick={addRule} className="text-[0.65rem] uppercase tracking-[0.22em] px-4 py-2 border border-[var(--ed-ink)] text-[var(--ed-ink)] hover:bg-[var(--ed-ink)] hover:text-[var(--ed-bg)] rounded-sm">
                    Add
                  </button>
                </div>
                <div className="space-y-1.5">
                  {form.house_rules.map((r, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 bg-[var(--ed-paper)] border border-[var(--ed-rule)] text-sm text-[var(--ed-ink-soft)] rounded-sm">
                      <span>{r}</span>
                      <button type="button" data-testid={`editor-remove-rule-${i}`} onClick={() => removeRule(i)} className="text-[var(--ed-ink-muted)] hover:text-red-500">
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {activeTab === "seo" && (
            <>
              <div>
                <FieldLabel>SEO title</FieldLabel>
                <Input data-testid="editor-seo-title" value={form.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} maxLength={70} />
              </div>
              <div>
                <FieldLabel>Meta description</FieldLabel>
                <Textarea data-testid="editor-seo-desc" rows={3} value={form.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} maxLength={160} />
              </div>
              <div>
                <FieldLabel>Open Graph image URL</FieldLabel>
                <Input data-testid="editor-og" value={form.og_image || ""} placeholder="/uploads/og.jpg" onChange={(e) => set("og_image", e.target.value)} />
              </div>
            </>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-7 py-5 border-t border-[var(--ed-rule)] bg-[var(--ed-paper)]/40 flex items-center justify-between gap-3 sticky bottom-0">
          <p className="text-[0.7rem] text-[var(--ed-ink-muted)]">
            {form.published ? "Visible on the public site once saved." : "Hidden from the public site (saved in studio only)."}
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="text-[0.65rem] uppercase tracking-[0.22em] px-4 py-2.5 border border-[var(--ed-rule)] text-[var(--ed-ink-soft)] hover:border-[var(--ed-ink)] hover:text-[var(--ed-ink)] rounded-sm">
              Cancel
            </button>
            <button
              data-testid="editor-save"
              type="submit"
              disabled={saving}
              className="text-[0.65rem] uppercase tracking-[0.22em] px-5 py-2.5 bg-[var(--ed-emerald)] text-white hover:bg-[var(--ed-emerald-deep)] rounded-sm flex items-center gap-2 disabled:opacity-50"
            >
              <Save size={12} /> {saving ? "Saving…" : (isNew ? "Create residence" : "Save changes")}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
